import { cn } from "@newxsystems/shared";
import { Icon } from "@templates/design-system";
import { getComponent } from "../../../../client";
import { RavioliMedia } from "../../../../modules/content/components/RavioliMedia/RavioliMedia";
import { RavioliRichText } from "../../../../modules/content/components/RavioliRichText/RavioliRichText";
import type { IFooterBlockProps } from "./FooterBlock.props";

export const FooterBlock = ({
  contact,
  copyright,
  description,
  logo,
  navigationGroups,
  parentClass,
  secondaryLinks,
  socialLinks,
}: IFooterBlockProps) => {
  if (
    !logo &&
    !description &&
    !contact?.email &&
    !contact?.phone &&
    !contact?.address &&
    !navigationGroups?.length &&
    !socialLinks?.length &&
    !secondaryLinks?.length &&
    !copyright
  )
    return null;
  const Link = getComponent("Link");
  const hasContact = Boolean(contact?.email || contact?.phone || contact?.address);
  const hasBrand = Boolean(logo || description || hasContact);
  const hasNavigation = Boolean(navigationGroups?.length);
  const hasSocial = Boolean(socialLinks?.length);
  const mainRegionCount = Number(hasBrand) + Number(hasNavigation) + Number(hasSocial);
  const mainColumns = {
    1: "lg:grid-cols-1",
    2: "lg:grid-cols-2",
    3: "lg:grid-cols-[1.2fr_2fr_0.8fr]",
  } as const;
  const navigationColumns = {
    1: "lg:grid-cols-1",
    2: "lg:grid-cols-2",
    3: "lg:grid-cols-3",
  } as const;
  const navigationColumnCount = Math.min(navigationGroups?.length || 1, 3) as 1 | 2 | 3;
  const linkClass =
    "inline-flex min-h-11 items-center font-ravioli-body text-sm text-ink-muted transition-colors hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring";
  return (
    <div className={`${parentClass || ""} border-t border-primary bg-collection text-ink`}>
      <div className="ravioli-container py-12 md:py-16">
        {mainRegionCount ? (
          <div
            className={cn(
              "grid gap-12 border-t border-primary/40 pt-8",
              mainColumns[mainRegionCount as keyof typeof mainColumns],
            )}
          >
            {hasBrand ? (
              <div>
                {logo ? (
                  <RavioliMedia
                    className="mb-6 h-11 w-48 bg-transparent [&>div]:border-0"
                    imageClassName="object-contain object-left"
                    media={logo}
                    sizes="192px"
                  />
                ) : null}
                {description ? (
                  <RavioliRichText
                    className="max-w-sm text-base text-ink-muted md:text-lg"
                    content={description}
                  />
                ) : null}
                {contact && hasContact ? (
                  <address className="mt-5 flex flex-col not-italic">
                    {contact.email ? (
                      <a className={linkClass} href={`mailto:${contact.email}`}>
                        {contact.email}
                      </a>
                    ) : null}
                    {contact.phone ? (
                      <a className={linkClass} href={`tel:${contact.phone}`}>
                        {contact.phone}
                      </a>
                    ) : null}
                    {contact.address ? (
                      <p className="mt-2 font-ravioli-body text-sm leading-6 text-ink-muted">
                        {contact.address}
                      </p>
                    ) : null}
                  </address>
                ) : null}
              </div>
            ) : null}
            {navigationGroups?.length ? (
              <nav
                aria-label="Footer"
                className={cn(
                  "grid gap-8 sm:grid-cols-2",
                  navigationColumns[navigationColumnCount],
                )}
              >
                {navigationGroups.map((group, index) => (
                  <div key={index}>
                    <h2 className="mb-3 font-ravioli-display text-sm font-bold">{group.header}</h2>
                    <ul>
                      {group.links.map((link, linkIndex) => (
                        <li key={linkIndex}>
                          <Link
                            aria-label={link.accessibilityLabel || undefined}
                            className={linkClass}
                            href={link.href}
                            rel={link.openInNewTab ? "noopener noreferrer" : undefined}
                            target={link.openInNewTab ? "_blank" : undefined}
                          >
                            {link.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </nav>
            ) : null}
            {socialLinks?.length ? (
              <div className="flex flex-wrap content-start gap-2">
                {socialLinks.map((item, index) => (
                  <Link
                    aria-label={item.link.accessibilityLabel || undefined}
                    className="inline-flex min-h-11 items-center rounded-full border border-primary bg-surface px-4 font-ravioli-body text-sm font-semibold text-primary hover:bg-primary hover:text-primary-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                    href={item.link.href}
                    key={index}
                    rel={item.link.openInNewTab ? "noopener noreferrer" : undefined}
                    target={item.link.openInNewTab ? "_blank" : undefined}
                  >
                    {item.icon ? <Icon className="mr-2 size-4" name={item.icon} /> : null}
                    {item.link.label}
                  </Link>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
        {secondaryLinks?.length || copyright ? (
          <div
            className={cn(
              "flex flex-col gap-3 border-t border-primary/40 pt-6 font-ravioli-body text-sm text-ink-muted sm:flex-row sm:items-center sm:justify-between",
              mainRegionCount && "mt-10 md:mt-12",
            )}
          >
            {secondaryLinks?.length ? (
              <div className="flex flex-wrap gap-x-5">
                {secondaryLinks.map((link, index) => (
                  <Link
                    aria-label={link.accessibilityLabel || undefined}
                    className={linkClass}
                    href={link.href}
                    key={index}
                    rel={link.openInNewTab ? "noopener noreferrer" : undefined}
                    target={link.openInNewTab ? "_blank" : undefined}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            ) : null}
            {copyright ? <p>{copyright}</p> : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

FooterBlock.displayName = "Block::FooterBlock";
